import { QueryClientProvider } from 'react-query';
import { useLocation } from '@reach/router';
import { QueryParamProvider } from 'use-query-params'
import { ReachAdapter } from 'use-query-params/adapters/reach';
import { LazyMotion, m } from 'framer-motion';

import Footer from './Footer'
import Header from './Header';
import Logo, { LOCAL_STORAGE_KEY_HOME_ANIMATION_VISIBLE } from './Atoms/Icons/Logo';

import { queryClient } from '../utils/api'
import { useLocalStorage } from '../utils/hooks/useLocalStorage';
import WP21CookieNotice from './CookieNotice/WP21CookieNotice';
import Scripts from './Scripts/Scripts';
import { TrustPilotProvider } from '../utils/ctx/TrustPilotContext';
import { useEffect, useState } from 'react';

const loadFramerMotionFeatures = () => import('../utils/framerMotionFeatures').then(res => res.default);

type LayoutProps = {
  children: React.ReactNode,
  hasStickyChildren?: boolean,
  bgColor?: string,
  hideFooter?: boolean,
  footerColor?: string,
}

const Layout = ({
  children,
  hasStickyChildren = false,
  bgColor = 'bg-blue',
  hideFooter = false,
  footerColor = 'bg-gray-100',
}: LayoutProps) => {
  const [isAnimationVisible,] = useLocalStorage(LOCAL_STORAGE_KEY_HOME_ANIMATION_VISIBLE, true);
  const [isTrustPilotLoaded, setIsTrustPilotLoaded] = useState(false);
  const [startAnimation, setStartAnimation] = useState(false);
  const { pathname } = useLocation();

  const transition = isAnimationVisible ? {
    ease: 'anticipate',
    delay: 0.25,
    duration: 0.5,
  } : {
    ease: 'anticipate',
    duration: 0.25,
  };

  useEffect(() => {
    // Use requestAnimationFrame to delay the animation start
    requestAnimationFrame(() => {
      setStartAnimation(true);
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <QueryParamProvider adapter={ReachAdapter}>
        <TrustPilotProvider isTrustPilotLoaded={isTrustPilotLoaded} setIsTrustPilotLoaded={setIsTrustPilotLoaded}>
          <LazyMotion features={loadFramerMotionFeatures}>
            {pathname !== '/' && <m.div
              className="fixed top-0 left-0 w-full z-101 bg-blue flex justify-center items-center"
              initial={{
                y: 0,
                height: '120%',
              }}
              animate={startAnimation ? {
                y: '100%',
                transitionEnd: {
                  display: "none",
                },
              } : {}}
              exit={{
                y: 0,
              }}
              transition={transition}
            >
              <Logo />
            </m.div>}
            <Header bgColor={bgColor} hasStickyChildren={hasStickyChildren} />
            <main className={`${!hasStickyChildren ? 'overflow-x-hidden' : ''} lg:scroll-smooth ${footerColor}`}>
              {children}
            </main>
            {!hideFooter && <Footer footerColor={footerColor} />}
            <WP21CookieNotice />
            <Scripts />
          </LazyMotion>
        </TrustPilotProvider>
      </QueryParamProvider>
    </QueryClientProvider>
  )
}

export default Layout
